import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { compareFamily } from '@domain/product'
import type { PricePoint, Product } from '@domain/product'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { ComparacaoDeFamilia } from './ComparacaoDeFamilia'

/**
 * A comparação entre os tamanhos de uma família.
 *
 * ## A pergunta é "o que compensa por litro", e a tela responde só isso
 *
 * *"A Coca-Cola normal tem vários tamanhos. No final, o que vai compensar é o
 * custo por litro."* A lista é ordenada pelo preço por unidade base, a mais
 * barata primeiro, e o número aparece **por litro** — não por mililitro. Os dois
 * dizem a mesma coisa; só um é lido na gôndola.
 *
 * ## O que a tela NÃO esconde
 *
 * Variação em outra base (ml contra g) e variação sem preço aparecem
 * **nomeadas**, fora da ordenação. Uma lista que as omitisse faria a pessoa
 * achar que viu a família inteira — e decidir por uma comparação parcial é pior
 * do que não comparar.
 *
 * ## Carrega o HISTÓRICO de cada variação, e não o resumo
 *
 * `Product.stats` não guarda "último preço por unidade base" — só o total da
 * última compra. Derivar dos pontos evita um campo novo no resumo, uma migração,
 * e produtos com resumo velho respondendo errado até alguém comprar de novo. São
 * poucas variações por família.
 */
export function Familia({
  produto,
  nomeDaFamilia,
}: {
  produto: Product
  /**
   * O nome da família, que quem carrega é o `SeletorDeFamilia` do cabeçalho.
   *
   * Recebido em vez de lido de novo: as duas leituras seriam a mesma consulta
   * na mesma tela, e a segunda chegaria um instante depois — tempo suficiente
   * para o `aria-label` da lista dizer "Tamanhos de família" e mudar sozinho
   * meio segundo depois.
   */
  nomeDaFamilia: string | null
}) {
  const { productRepository } = useFinanceRepositories()

  /**
   * As irmãs, **junto de para qual família elas valem**. Sem isso, trocar de
   * família mostraria por um render os tamanhos da família ANTERIOR sob o nome
   * da nova — e limpar num efeito é justamente o que o lint proíbe, com razão:
   * o descarte precisa acontecer no render, não um passo depois.
   */
  const [carregadas, setCarregadas] = useState<{
    para: string | null
    irmas: Product[]
    pontos: Map<string, PricePoint[]>
  }>({ para: null, irmas: [], pontos: new Map() })
  const familyId = produto.familyId

  useEffect(() => {
    if (familyId === null) return
    let vivo = true
    void productRepository
      .list()
      .then(async (todos) => {
        const daFamilia = todos.filter((p) => p.familyId === familyId)
        const historicos = await Promise.all(
          daFamilia.map(async (p) => [p.id, await productRepository.priceHistory(p.id)] as const),
        )
        if (!vivo) return
        setCarregadas({ para: familyId, irmas: daFamilia, pontos: new Map(historicos) })
      })
      .catch((e: unknown) => {
        console.error('Familia: a comparação não carregou', e)
      })
    return () => {
      vivo = false
    }
  }, [productRepository, familyId])

  // O dado velho é descartado AQUI, no render. Ver `carregadas`.
  const daFamiliaAtual =
    carregadas.para === familyId
      ? carregadas
      : { irmas: [], pontos: new Map<string, PricePoint[]>() }
  const comparacao = compareFamily(daFamiliaAtual.irmas, daFamiliaAtual.pontos)
  const porPeso = produto.unit === 'g' || produto.unit === 'kg'

  return (
    // Sem `aria-label` e sem `<h2>`: desde a decisão 239 quem envolve isto é
    // uma `Dobra` chamada "Tamanhos", e nomear os dois daria duas regiões com o
    // mesmo nome — ambíguo para quem navega por região, e o teste pegou.
    <div>
      {familyId === null ? (
        <p className="text-xs text-[var(--text-muted)]">
          {`Sem família, este produto é comparado sozinho. Coloque os outros tamanhos na mesma família para saber qual compensa por ${porPeso ? 'quilo' : 'litro'}.`}
        </p>
      ) : (
        <>
          <ComparacaoDeFamilia
            comparacao={comparacao}
            nome={nomeDaFamilia ?? 'família'}
            destaqueId={produto.id}
          />
          <Link to="/produtos" className="mt-2 inline-block text-xs underline">
            Ver todos os grupos
          </Link>
        </>
      )}
    </div>
  )
}
